/**
 * App-access group gate, resolved from the B2C id token.
 *
 * The claim shape is the tenant's, not ours: `roles` is an array of JSON
 * *strings*, each `{"id": "<guid>"}`. Mirrors `parseRoleIds` in DataNovaProxy
 * and `userRoleAccessHook.ts` in MicroUI.CMS - both of which resolve several
 * named roles this way. This app only needs the one group id, so there is no
 * role-name mapping here, just membership in `VITE_ARDASHBOARD_ADMIN_GROUP_ID`.
 */

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch (error) {
    console.error('Could not decode the token payload:', error);
    return null;
  }
};

/** Pulls the role/group ids out of the `roles` claim. */
export const parseRoleIds = (claim: unknown): string[] => {
  if (!Array.isArray(claim)) return [];

  return claim
    .map((entry) => {
      if (typeof entry !== 'string') return null;
      try {
        return (JSON.parse(entry) as { id?: string }).id ?? null;
      } catch {
        return null;
      }
    })
    .filter((id): id is string => Boolean(id));
};

/**
 * Whether the app-access gate is turned on. Enabled by default in every
 * `.env.<mode>` file - relies on B2C's custom policy actually surfacing
 * `VITE_ARDASHBOARD_ADMIN_GROUP_ID`'s membership in the `roles` claim.
 */
export const isAppAccessGateEnabled = (): boolean =>
  import.meta.env.VITE_ARDASHBOARD_ACCESS_GATE_ENABLED === 'true';

/**
 * Whether the token carries membership in the app-access group, when the gate
 * is enabled. Fails closed (denies) rather than open on misconfiguration -
 * the gate exists to restrict access, so a missing group id should not
 * silently grant it to everyone.
 */
export const hasAppAccessGroup = (token: string | null): boolean => {
  if (!isAppAccessGateEnabled()) return true;

  const groupId = import.meta.env.VITE_ARDASHBOARD_ADMIN_GROUP_ID;
  if (!groupId) {
    console.error(
      'VITE_ARDASHBOARD_ACCESS_GATE_ENABLED is "true" but VITE_ARDASHBOARD_ADMIN_GROUP_ID is not set; denying access.',
    );
    return false;
  }
  if (!token) return false;

  const claims = decodeJwtPayload(token);
  if (!claims) return false;

  return parseRoleIds(claims.roles).includes(groupId);
};
